import { HTMLMotionProps, motion } from 'framer-motion'
import { useMemo } from 'react'
import { match } from 'ts-pattern'

type Direction = 'left' | 'right' | 'top' | 'bottom'

type SlideInProps = {
	children: React.ReactNode
	className?: string
	from: Direction
	duration?: number
	onView?: boolean
	offset?: number
	viewportMargin?: string
} & HTMLMotionProps<'div'>

export function SlideIn({
	children,
	from,
	onView = true,
	offset = 100,
	viewportMargin = '0px 0px -100px 0px',
	transition,
	...props
}: SlideInProps) {
	const initial = useMemo(
		() =>
			match(from)
				.with('left', () => ({ x: -offset, opacity: 0 }))
				.with('right', () => ({ x: offset, opacity: 0 }))
				.with('top', () => ({ y: -offset, opacity: 0 }))
				.with('bottom', () => ({ y: offset, opacity: 0 }))
				.exhaustive(),
		[from, offset]
	)

	const animate = useMemo(
		() =>
			match(from)
				.with('left', 'right', () => ({ x: 0, opacity: 1 }))
				.with('top', 'bottom', () => ({ y: 0, opacity: 1 }))
				.exhaustive(),
		[from]
	)

	return (
		<motion.div
			initial={initial}
			transition={{ duration: 0.5, ...transition }}
			{...(onView ? { whileInView: animate } : { animate })}
			viewport={{ once: onView, margin: viewportMargin }}
			style={{ willChange: 'transform, opacity' }}
			{...props}
		>
			{children}
		</motion.div>
	)
}
